/**
 * Linear URL-import junk filter. One forward pass, capped input and block count.
 * Keep in sync with functions/src/Scapper/scrapJunkFilter.ts.
 */

const MAX_INPUT_CHARS = 120_000;
const MAX_BLOCKS = 100;
const MAX_LINK_ONLY_CHARS = 80;
const MAX_LINK_ONLY_WORDS = 8;

const BLOCK_RE = /<(p|li|figcaption|h[1-6])\b[^>]*>[\s\S]*?<\/\1>/gi;
const ANCHOR_RE = /<a\b[^>]*>[\s\S]*?<\/a>/gi;

function decodeNumericEntities(value: string): string {
    return value
        .replace(/&#(\d+);/g, (_match, code) => String.fromCharCode(Number(code)))
        .replace(/&#x([0-9a-f]+);/gi, (_match, code) => String.fromCharCode(parseInt(code, 16)));
}

export function plainScrapText(value: unknown): string {
    const withoutTags = String(value ?? '').replace(/<[^>]+>/g, ' ');
    return decodeNumericEntities(withoutTags)
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;|&apos;/gi, "'")
        .replace(/[\u200B\u2060\u00a0]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function normalizeScrapKey(value: unknown): string {
    return plainScrapText(value)
        .toLocaleLowerCase('cs')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim();
}

/** A short credit or "read more" line: a few words, a colon, and no sentence. */
export function isColonCreditLine(text: unknown): boolean {
    const plain = plainScrapText(text);
    if (!plain || plain.length > 160 || /[.!?]/.test(plain)) {
        return false;
    }
    const match = /^((?:\p{L}+\s+){0,3}\p{L}+)\s*:/u.exec(plain);
    if (!match) {
        return false;
    }
    const words = match[1].split(/\s+/).length;
    if (words < 1 || words > 4) {
        return false;
    }
    // A heading can use a colon before a clause. A credit is only a short name:
    // at most four words after the colon. A longer clause stays a heading.
    const after = plain.slice(match[0].length).trim();
    const afterWords = after.split(/\s+/).filter(Boolean);
    return afterWords.length > 0 && afterWords.length <= 4;
}

/** URL captions and short colon credits. Not a list of site phrases. */
export function looksLikeScrapeBoilerplateBody(text: unknown): boolean {
    const plain = plainScrapText(text);
    if (!plain) {
        return false;
    }
    if (looksLikeCrossDomainAltCaption(plain)) {
        return true;
    }
    return isColonCreditLine(plain);
}

/** Alt or caption whose whole text is a host, a URL, or a host plus step numbers. */
export function looksLikeCrossDomainAltCaption(text: unknown): boolean {
    const plain = plainScrapText(text);
    if (!plain || plain.length > 160) {
        return false;
    }
    if (/^(?:https?:\/\/|www\.)[a-z0-9.-]+\.[a-z]{2,}(?:\/\S*)?\s*$/i.test(plain)) {
        return true;
    }
    if (/^(?:https?:\/\/|www\.)[a-z0-9.-]+\.[a-z]{2,}\b/i.test(plain)) {
        const rest = plain.replace(/^(?:https?:\/\/|www\.)[a-z0-9.-]+\.[a-z]{2,}(?:\/\S*)?/i, '').trim();
        return !rest || /^(?:\d+\.\s*)+$/.test(rest);
    }
    return false;
}

export function isKeywordOnlyScrapLine(text: unknown, mainKeyword: unknown): boolean {
    const line = normalizeScrapKey(text);
    const key = normalizeScrapKey(mainKeyword);
    if (!line || !key) {
        return false;
    }
    return line === key;
}

/** True when this whole line is scrap chrome. A longer sentence that only mentions the keyword stays. */
export function isScrapJunkLine(text: unknown, mainKeyword = ''): boolean {
    const plain = plainScrapText(text);
    if (!plain) {
        return false;
    }
    if (looksLikeScrapeBoilerplateBody(plain)) {
        return true;
    }
    return isKeywordOnlyScrapLine(plain, mainKeyword);
}

function rootTag(html: string): string {
    const match = /^\s*<([a-z0-9]+)/i.exec(html);
    return match ? match[1].toLowerCase() : '';
}

function isHeadingBlock(block: string): boolean {
    return /^h[1-6]$/.test(rootTag(block));
}

/** One anchor and no words outside it. Headings and list items are not paragraphs. */
function isSingleAnchorOnly(html: string): boolean {
    const tag = rootTag(html);
    if (tag === 'li' || tag === 'figcaption' || isHeadingBlock(html)) {
        return false;
    }
    if (tag && tag !== 'a' && tag !== 'p' && tag !== 'div' && tag !== 'span') {
        return false;
    }
    const anchors = html.match(new RegExp(ANCHOR_RE.source, 'gi'));
    if (!anchors || anchors.length !== 1) {
        return false;
    }
    const without = html.replace(new RegExp(ANCHOR_RE.source, 'i'), ' ');
    return plainScrapText(without).length === 0;
}

/** Short anchor text with no sentence punctuation. A real sentence stays. */
function isShortNonSentence(text: string): boolean {
    const plain = plainScrapText(text);
    if (!plain || plain.length > MAX_LINK_ONLY_CHARS || /[.!?]/.test(plain)) {
        return false;
    }
    return plain.split(/\s+/).length <= MAX_LINK_ONLY_WORDS;
}

/**
 * Standalone paragraph that is only a link (optional whitespace).
 * Keyword match still drops it. Without a keyword, only a short non-sentence drops.
 */
function isLinkOnlyScrapParagraph(html: string, mainKeyword: string): boolean {
    if (!isSingleAnchorOnly(html)) {
        return false;
    }
    const text = plainScrapText(html);
    if (!text) {
        return false;
    }
    if (isKeywordOnlyScrapLine(text, mainKeyword)) {
        return true;
    }
    return isShortNonSentence(text);
}

export function scrapSectionHasBody(paragraph: unknown): boolean {
    return plainScrapText(paragraph).length > 0;
}

const SPACE_RE = /\s/;
const LETTER_RE = /^\p{L}$/u;
const TAG_NAME_RE = /[a-z0-9]/i;
const POINTER_STOP = '<.!?';
const MAX_POINTER_TAIL = 180;
const MAX_POINTER_WORDS = 4;

function isSpaceAt(value: string, index: number): boolean {
    return SPACE_RE.test(value.charAt(index));
}

/** Start of the letter run (whole code points) that ends at `end`. */
function letterRunStart(value: string, end: number): number {
    let index = end;
    while (index > 0) {
        let start = index - 1;
        const low = value.charCodeAt(start);
        if (low >= 0xdc00 && low <= 0xdfff && start > 0) {
            const high = value.charCodeAt(start - 1);
            if (high >= 0xd800 && high <= 0xdbff) {
                start -= 1;
            }
        }
        if (!LETTER_RE.test(value.slice(start, index))) {
            break;
        }
        index = start;
    }
    return index;
}

/** Where the closing tags at the very end start, whitespace between them allowed. The length when there are none. */
function closingTagTailStart(value: string): number {
    let start = value.length;
    for (;;) {
        let end = start;
        while (end > 0 && isSpaceAt(value, end - 1)) {
            end -= 1;
        }
        if (end === 0 || value.charAt(end - 1) !== '>') {
            return start;
        }
        let name = end - 1;
        while (name > 0 && TAG_NAME_RE.test(value.charAt(name - 1))) {
            name -= 1;
        }
        if (name === end - 1 || name < 2 || value.charAt(name - 1) !== '/' || value.charAt(name - 2) !== '<') {
            return start;
        }
        start = name - 2;
    }
}

/** Start of up to four words that end right before the colon (whitespace allowed before it). -1 without a word. */
function pointerLabelStart(value: string, colon: number): number {
    let end = colon;
    while (end > 0 && isSpaceAt(value, end - 1)) {
        end -= 1;
    }
    let start = -1;
    for (let words = 0; words < MAX_POINTER_WORDS; words++) {
        const wordStart = letterRunStart(value, end);
        if (wordStart === end) {
            break;
        }
        start = wordStart;
        let gap = wordStart;
        while (gap > 0 && isSpaceAt(value, gap - 1)) {
            gap -= 1;
        }
        if (gap === wordStart) {
            break;
        }
        end = gap;
    }
    return start;
}

/** `(?:<br\s*\/?>\s*)+$` without the regex, which backtracks on long runs of breaks. */
function stripTrailingBreaks(value: string): string {
    let cut = value.length;
    let end = value.length;
    while (end > 0 && value.charAt(end - 1) === '>') {
        let index = end - 1;
        if (index > 0 && value.charAt(index - 1) === '/') {
            index -= 1;
        }
        while (index > 0 && isSpaceAt(value, index - 1)) {
            index -= 1;
        }
        if (index < 3 || value.slice(index - 3, index).toLowerCase() !== '<br') {
            break;
        }
        cut = index - 3;
        end = cut;
        while (end > 0 && isSpaceAt(value, end - 1)) {
            end -= 1;
        }
    }
    return value.slice(0, cut);
}

/**
 * Drop one trailing credit/read-more clause ("a few words: short tail") and keep the closing tags.
 *
 * Same result as matching /((?:\p{L}+\s+){0,3}\p{L}+\s*:\s*[^<.!?]{1,180})\s*((?:<\/[a-z0-9]+>\s*)*)$/iu,
 * but linear: that regex retried every start position over a long run of letters
 * without spaces, 42 s on 60k CJK characters (#30946).
 */
export function stripTrailingPointer(block: string): string {
    const tail = closingTagTailStart(block);
    let floor = -1;
    let lastText = -1;
    for (let index = tail - 1; index >= 0; index--) {
        const char = block.charAt(index);
        if (lastText < 0 && !SPACE_RE.test(char)) {
            lastText = index;
        }
        if (POINTER_STOP.includes(char)) {
            floor = index;
            break;
        }
    }
    // The leftmost start wins, as in the regex. Label runs end at the colon, so
    // they never overlap and the scan stays linear.
    for (let colon = floor + 1; colon < tail; colon++) {
        if (block.charAt(colon) !== ':') {
            continue;
        }
        let first = colon + 1;
        while (first < tail && isSpaceAt(block, first)) {
            first += 1;
        }
        if (first === tail) {
            if (tail - colon - 1 < 1) {
                continue;
            }
        } else {
            // The regex counts code points; a surrogate pair is two units.
            const units = lastText - first + 1;
            if (units > MAX_POINTER_TAIL * 2
                || (units > MAX_POINTER_TAIL && Array.from(block.slice(first, lastText + 1)).length > MAX_POINTER_TAIL)) {
                continue;
            }
        }
        const start = pointerLabelStart(block, colon);
        if (start < 0) {
            continue;
        }
        const label = plainScrapText(block.slice(start, colon));
        const words = label.split(/\s+/).filter(Boolean);
        if (words.length < 1 || words.length > MAX_POINTER_WORDS || plainScrapText(block.slice(start, tail)).length > 280) {
            return block;
        }
        return stripTrailingBreaks(block.slice(0, start).trimEnd()) + block.slice(tail);
    }
    return block;
}

function cleanBlock(block: string, mainKeyword: string): string {
    const stripped = stripTrailingPointer(block).replace(/<a\b[^>]*>\s*<\/a>/gi, '');
    if (isLinkOnlyScrapParagraph(stripped, mainKeyword)) {
        return '';
    }
    const text = plainScrapText(stripped);
    if (!text || isScrapJunkLine(text, mainKeyword)) {
        return '';
    }
    return stripped;
}

function cleanLooseText(chunk: string, mainKeyword: string): string {
    if (!chunk) {
        return '';
    }
    if (isLinkOnlyScrapParagraph(chunk, mainKeyword)) {
        return '';
    }
    const text = plainScrapText(chunk);
    if (!text) {
        return chunk;
    }
    const stripped = stripTrailingPointer(chunk);
    if (isLinkOnlyScrapParagraph(stripped, mainKeyword)) {
        return '';
    }
    const strippedText = plainScrapText(stripped);
    if (!strippedText || isScrapJunkLine(strippedText, mainKeyword)) {
        return chunk.replace(/[^<>]+/g, '');
    }
    return stripped === chunk ? chunk : stripped;
}

function filterPlainScrap(text: string, mainKeyword: string): string {
    const lines = text.split('\n');
    const kept: string[] = [];
    const limit = Math.min(lines.length, MAX_BLOCKS);
    for (let i = 0; i < limit; i++) {
        const stripped = stripTrailingPointer(lines[i]);
        if (isLinkOnlyScrapParagraph(stripped, mainKeyword)) {
            continue;
        }
        const plain = plainScrapText(stripped);
        if (!plain || isScrapJunkLine(plain, mainKeyword)) {
            continue;
        }
        kept.push(stripped.trim());
    }
    return kept.join('\n');
}

function hasVisibleText(value: string): boolean {
    return plainScrapText(value).length > 0;
}

export function cleanScrapedQuestions<T extends {question?: unknown; answer?: unknown}>(
    rows: T[] | null | undefined
): T[] {
    if (!Array.isArray(rows)) {
        return [];
    }
    const out: T[] = [];
    const limit = Math.min(rows.length, 40);
    for (let i = 0; i < limit; i++) {
        const row = rows[i];
        const question = plainScrapText(row?.question);
        if (!question || !plainScrapText(row?.answer)) {
            continue;
        }
        out.push({...row, question});
    }
    return out;
}

export function filterScrapJunkContent(raw: unknown, mainKeyword = ''): string {
    const source = String(raw ?? '').replace(/&nbsp;|&#160;|&#xa0;/gi, ' ');
    if (!source.trim()) {
        return '';
    }
    const tableOnly = source.trim();
    if (/^<table\b/i.test(tableOnly) && /<\/table>$/i.test(tableOnly)) {
        return tableOnly;
    }
    const capped = source.length > MAX_INPUT_CHARS ? source.slice(0, MAX_INPUT_CHARS) : source;
    const overflow = source.length > MAX_INPUT_CHARS ? source.slice(MAX_INPUT_CHARS) : '';
    if (!/<(?:p|li|figcaption|h[1-6])\b/i.test(capped)) {
        return filterPlainScrap(capped, mainKeyword) + overflow;
    }
    const matches = Array.from(capped.matchAll(new RegExp(BLOCK_RE.source, 'gi')));
    let out = '';
    let held = '';
    let last = 0;
    const limit = Math.min(matches.length, MAX_BLOCKS);
    const takeLoose = (chunk: string): void => {
        const cleaned = cleanLooseText(chunk, mainKeyword);
        if (!cleaned) {
            return;
        }
        if (hasVisibleText(cleaned)) {
            out += held + cleaned;
            held = '';
            return;
        }
        if (held) {
            held += cleaned;
        } else {
            out += cleaned;
        }
    };
    for (let i = 0; i < limit; i++) {
        const match = matches[i];
        const index = match.index ?? 0;
        takeLoose(capped.slice(last, index));
        const block = match[0];
        last = index + block.length;
        const cleaned = cleanBlock(block, mainKeyword);
        if (isHeadingBlock(block)) {
            if (hasVisibleText(cleaned)) {
                held = cleaned;
            }
            continue;
        }
        if (!hasVisibleText(cleaned)) {
            continue;
        }
        out += held + cleaned;
        held = '';
    }
    if (limit < matches.length) {
        const rest = capped.slice(last);
        out += hasVisibleText(rest) ? held + rest : rest;
        held = '';
    } else {
        takeLoose(capped.slice(last));
    }
    return out.replace(/<(ul|ol)\b[^>]*>\s*<\/\1>/gi, '').replace(/<p\b[^>]*>\s*<\/p>/gi, '').trim() + overflow;
}

export function sanitizeImportedImageAlt(alt: unknown): string {
    const text = String(alt ?? '');
    if (!text.trim() || isScrapJunkLine(text)) {
        return '';
    }
    return text;
}
